import axios from "axios";
import { API } from "@/lib/api";

const cfg = { withCredentials: true };

export const readerMe = () => axios.get(`${API}/reader/me`, cfg).then((r) => r.data);
export const readerRegister = (body) => axios.post(`${API}/reader/auth/register`, body, cfg).then((r) => r.data);
export const readerLogin = (body) => axios.post(`${API}/reader/auth/login`, body, cfg).then((r) => r.data);
export const readerGoogle = (sessionId) =>
  axios.post(`${API}/reader/auth/google`, { session_id: sessionId }, cfg).then((r) => r.data);
export const readerLogout = () => axios.post(`${API}/reader/auth/logout`, {}, cfg);
export const readerReset = (body) => axios.post(`${API}/reader/auth/reset`, body, cfg).then((r) => r.data);
export const forgotPassword = (email) => axios.post(`${API}/reader/auth/forgot`, { email }, cfg).then((r) => r.data);
export const resetWithEmail = (token, newPassword) =>
  axios.post(`${API}/reader/auth/reset-email`, { token, new_password: newPassword }, cfg).then((r) => r.data);
export const fetchAccount = () => axios.get(`${API}/account`, cfg).then((r) => r.data);
export const changeName = (name) => axios.put(`${API}/account/name`, { name }, cfg).then((r) => r.data);
export const changePassword = (body) => axios.put(`${API}/account/password`, body, cfg).then((r) => r.data);
export const changeEmail = (body) => axios.put(`${API}/account/email`, body, cfg).then((r) => r.data);
export const sendVerification = () => axios.post(`${API}/account/verify-email/send`, {}, cfg).then((r) => r.data);
export const confirmEmail = (token) => axios.post(`${API}/account/verify-email`, { token }, cfg).then((r) => r.data);
export const newRecoveryCode = () => axios.post(`${API}/reader/auth/recovery-code`, {}, cfg).then((r) => r.data);
export const setBookmark = (bookId, page, on) =>
  axios.put(`${API}/reader/bookmarks`, { book_id: bookId, page, on }, cfg).then((r) => r.data.bookmarks);
export const addHighlight = (bookId, page, text, para = null, start = null) =>
  axios.post(`${API}/reader/highlights`, { book_id: bookId, page, text, para, start }, cfg).then((r) => r.data.highlights);
export const removeHighlight = (bookId, id) =>
  axios.delete(`${API}/reader/highlights/${bookId}/${id}`, cfg).then((r) => r.data.highlights);
export const daysUntil = (iso) => Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));
export const fetchLibrary = () => axios.get(`${API}/reader/library`).then((r) => r.data.books);
export const completeReaderPass = (orderId) =>
  axios.post(`${API}/reader/pass/complete`, { order_id: orderId }, cfg).then((r) => r.data);
export const completeGift = (body) => axios.post(`${API}/reader/gift/complete`, body).then((r) => r.data);
export const redeemGift = (code) => axios.post(`${API}/reader/gift/redeem`, { code }, cfg).then((r) => r.data);
export const giftStatus = (code) => axios.get(`${API}/reader/gift/status/${encodeURIComponent(code)}`).then((r) => r.data);
export const openBook = (bookId) => axios.post(`${API}/reader/open`, { book_id: bookId }, cfg).then((r) => r.data);
export const fetchPage = (bookId, page) =>
  axios.get(`${API}/reader/books/${bookId}/pages/${page}`, cfg).then((r) => r.data);
export const saveProgress = (body) => axios.put(`${API}/reader/progress`, body, cfg);
export const sendStats = (body) => axios.post(`${API}/reader/stats`, body, cfg);
export const setTimezone = (tz) => axios.put(`${API}/account/timezone`, { tz }, cfg);
export const setReminders = (enabled) => axios.put(`${API}/account/reminders`, { enabled }, cfg).then((r) => r.data);

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const localDate = () => ymd(new Date());

// Current streak counts back from today (or yesterday, if today isn't read yet).
export function readingStreak(days = []) {
  const set = new Set(days);
  const cursor = new Date();
  const readToday = set.has(ymd(cursor));
  if (!readToday) cursor.setDate(cursor.getDate() - 1);
  let current = 0;
  while (set.has(ymd(cursor))) {
    current += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  let best = 0;
  let run = 0;
  let prev = null;
  [...set].sort().forEach((d) => {
    const t = new Date(`${d}T12:00:00`);
    run = prev && Math.round((t - prev) / 86400000) === 1 ? run + 1 : 1;
    best = Math.max(best, run);
    prev = t;
  });
  return { current, best, readToday };
}

export function formatDuration(seconds = 0) {
  const mins = Math.round(seconds / 60);
  if (seconds > 0 && mins === 0) return "<1 min";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}
export const fetchAudio = (bookId, page, voice, prefetch = false) =>
  axios
    .get(`${API}/reader/books/${bookId}/pages/${page}/audio`, {
      ...cfg,
      params: prefetch ? { voice, prefetch: true } : { voice },
      responseType: "blob",
      timeout: 120000,
    })
    .then((r) => r.data);

export function errorDetail(e) {
  const detail = e?.response?.data?.detail;
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

export function googleSignIn() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/reader";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
